import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Send, Check, Copy, Share2, Sparkles, Loader2, Save, RotateCcw, Undo2, Upload, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

// Frontend fallback — matches the backend default so the page still renders
// something meaningful if the CMS call fails while offline.
const FALLBACK = {
  title: 'Save the Date — Mary & Arrington',
  weddingDate: 'July 31, 2027',
  message: 'With hearts full of grace, we joyfully invite you to celebrate our wedding day. Come share in the love, laughter, and prayers as we begin forever together.',
  ctaLabel: 'Wedding details & RSVP',
  siteUrl: 'https://gracemadeus.com',
  previewImageUrl: 'https://gracemadeus.com/video/poster.jpg',
};

// Composes the exact message that the native share sheet will hand to the
// recipient's app. Kept in one place so the "message preview" card and the
// share/copy actions always stay in sync.
const composeShareMessage = (c) => (
  `${c.title || ''}\n${c.weddingDate || ''}\n\n${c.message || ''}\n\n${c.siteUrl || ''}`.trim()
);

const resolveImage = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

/**
 * AdminSaveTheDate
 *
 * Fully CMS-backed share-card editor. Every field lives in the
 * `save_the_date_content` singleton — edits are persisted immediately on
 * Save and picked up the next time the couple shares the site. No
 * redeploy is required for content changes.
 */
const AdminSaveTheDate = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [shareState, setShareState] = useState('idle');
  const [copyState, setCopyState] = useState('idle');
  const fileInputRef = useRef(null);

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/save-the-date/content`);
      const data = { ...FALLBACK, ...(res.data || {}) };
      setInitial(clone(data));
      setContent(clone(data));
    } catch { setError('Could not load Save the Date content.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (fn) => setContent((prev) => { const n = clone(prev); fn(n); return n; });
  const dirty = useMemo(
    () => content && initial && JSON.stringify(content) !== JSON.stringify(initial),
    [content, initial]
  );

  const save = async () => {
    if (!dirty) return;
    setSaving(true); setError('');
    try {
      const payload = { ...content };
      delete payload.id; delete payload.updated_at;
      const res = await axios.put(`${API}/admin/save-the-date/content`, payload, { headers: authHeader() });
      const data = { ...FALLBACK, ...(res.data || {}) };
      setInitial(clone(data));
      setContent(clone(data));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save.');
    }
    finally { setSaving(false); }
  };

  const revert = () => setContent(clone(initial));

  const resetDefaults = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/admin/save-the-date/content/reset`, {}, { headers: authHeader() });
      await load();
      setResetOpen(false);
    } catch { setError('Failed to reset content.'); }
    finally { setResetting(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploading(true); setUploadError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/save-the-date/image`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const nextUrl = res.data?.previewImageUrl;
      if (!nextUrl) throw new Error('No previewImageUrl in response');
      // Server has already persisted this URL on the singleton — reflect
      // that here so `dirty` stays accurate.
      setInitial((prev) => ({ ...(prev || {}), previewImageUrl: nextUrl }));
      setContent((prev) => ({ ...(prev || {}), previewImageUrl: nextUrl }));
    } catch (err) {
      setUploadError(err?.response?.data?.detail || 'Upload failed. Please try a different image.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const flash = (setter, val = 'copied') => {
    setter(val);
    setTimeout(() => setter('idle'), 2400);
  };

  const share = async () => {
    if (!content) return;
    const payload = {
      title: content.title || FALLBACK.title,
      text:  composeShareMessage(content),
      url:   content.siteUrl || FALLBACK.siteUrl,
    };
    try {
      if (canNativeShare) {
        await navigator.share(payload);
        flash(setShareState, 'shared');
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }
    await copyMessage();
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(composeShareMessage(content));
      flash(setCopyState);
    } catch {
      window.prompt('Copy the message below and share it with your guest:', composeShareMessage(content));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(content?.siteUrl || FALLBACK.siteUrl);
      flash(setCopyState);
    } catch { /* noop */ }
  };

  if (loading || !content) {
    return (
      <div className="bg-white rounded-2xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2" data-testid="admin-save-the-date-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Save the Date…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-save-the-date">
      {/* Sticky action bar */}
      <div className="bg-white rounded-2xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Sparkles className="w-6 h-6 text-[#C8A951] mt-1" />
          <div>
            <h2 className="font-serif text-2xl text-[#3B2F2F]">Save the Date</h2>
            <p className="text-xs text-[#8A7A7A] max-w-xl">
              Every field is editable. Save Changes writes to the database — no redeploy needed. The
              share sheet, message preview, and social link unfurl all use this content the next time
              you share.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetOpen(true)} className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1" data-testid="save-the-date-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={revert} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="save-the-date-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60" data-testid="save-the-date-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}

      <div className="grid md:grid-cols-[1.05fr_1fr] gap-4">
        {/* Editable fields */}
        <div className="bg-white rounded-2xl shadow-sm p-5 md:p-6 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Title</label>
            <Input
              value={content.title || ''}
              onChange={(e) => patch((d) => { d.title = e.target.value; })}
              data-testid="save-the-date-title-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Wedding date</label>
            <Input
              value={content.weddingDate || ''}
              onChange={(e) => patch((d) => { d.weddingDate = e.target.value; })}
              placeholder="July 31, 2027"
              data-testid="save-the-date-date-input"
            />
            <p className="text-[11px] text-[#8A7A7A] mt-1">Displayed as-is in the share message and preview card.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Main message</label>
            <Textarea
              rows={4}
              value={content.message || ''}
              onChange={(e) => patch((d) => { d.message = e.target.value; })}
              data-testid="save-the-date-message-input"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">CTA label</label>
              <Input
                value={content.ctaLabel || ''}
                onChange={(e) => patch((d) => { d.ctaLabel = e.target.value; })}
                placeholder="Wedding details & RSVP"
                data-testid="save-the-date-cta-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Website link</label>
              <Input
                value={content.siteUrl || ''}
                onChange={(e) => patch((d) => { d.siteUrl = e.target.value; })}
                placeholder="https://gracemadeus.com"
                data-testid="save-the-date-url-input"
              />
            </div>
          </div>

          {/* Preview image uploader */}
          <div className="border border-[#E5E0D8] rounded-lg p-4" data-testid="save-the-date-image-card">
            <p className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-3 flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5 text-[#C8A951]" /> Preview image (link unfurl)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
              <div className="relative rounded-lg overflow-hidden bg-[#F8F5EF] border border-[#E5E0D8]">
                {content.previewImageUrl ? (
                  <img
                    src={resolveImage(content.previewImageUrl)}
                    alt="Save the Date preview"
                    className="block w-full h-auto"
                    data-testid="save-the-date-image-preview"
                  />
                ) : (
                  <div className="w-full aspect-[16/9] flex items-center justify-center text-[#8A7A7A] text-xs">No image set</div>
                )}
              </div>
              <div className="space-y-3">
                <Input
                  value={content.previewImageUrl || ''}
                  onChange={(e) => patch((d) => { d.previewImageUrl = e.target.value; })}
                  placeholder="/api/gallery/files/... or https://…"
                  data-testid="save-the-date-image-url-input"
                />
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={uploadImage}
                    className="hidden"
                    data-testid="save-the-date-image-file-input"
                  />
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white disabled:opacity-60"
                    data-testid="save-the-date-image-upload-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                    {uploading ? 'Uploading…' : 'Upload new image'}
                  </Button>
                </div>
                {uploadError && <p className="text-red-500 text-xs">{uploadError}</p>}
                <p className="text-[11px] text-[#8A7A7A] leading-relaxed">
                  Recommended: landscape 1200 × 630 px or larger. Uploaded images are re-encoded to WebP
                  and stored on the server volume, so they survive redeploys. Social platforms will pick
                  up the new image on their next crawl of <code>gracemadeus.com/api/save-the-date/preview-image</code>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Live preview + share actions */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-6" data-testid="save-the-date-preview">
            <p className="text-[10px] uppercase tracking-widest text-[#8A7A7A] mb-3">Live message preview</p>
            <div className="rounded-lg bg-white border border-[#EFEAE0] shadow-sm overflow-hidden">
              {content.previewImageUrl && (
                <img
                  src={resolveImage(content.previewImageUrl)}
                  alt="Save the Date preview"
                  className="block w-full h-auto"
                  data-testid="save-the-date-preview-image"
                />
              )}
              <div className="p-4 md:p-5">
                <p className="font-cursive text-[#C8A951] text-2xl leading-none mb-1">Save the Date</p>
                <p className="font-serif text-[#3B2F2F] text-lg leading-tight mb-2" data-testid="save-the-date-preview-title">{content.title || FALLBACK.title}</p>
                <p className="text-xs uppercase tracking-[0.28em] text-[#8A7A7A] mb-3" data-testid="save-the-date-preview-date">{content.weddingDate || FALLBACK.weddingDate}</p>
                <p className="text-[#5A4A4A] text-sm leading-relaxed" data-testid="save-the-date-preview-message">
                  {content.message || FALLBACK.message}
                </p>
                {content.siteUrl && (
                  <a href={content.siteUrl} target="_blank" rel="noreferrer"
                     className="mt-3 inline-block text-[#C8A951] hover:text-[#B09240] text-sm font-medium underline underline-offset-4"
                     data-testid="save-the-date-preview-cta">
                    {content.ctaLabel || FALLBACK.ctaLabel} — {content.siteUrl}
                  </a>
                )}
              </div>
            </div>
            <p className="text-[11px] text-[#8A7A7A] mt-3 leading-relaxed">
              This is the exact message that will appear in the native share sheet. The preview image
              is attached automatically by the recipient's app when they see the link.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-5 md:p-6 flex flex-col gap-3">
            <button
              onClick={share}
              disabled={dirty}
              title={dirty ? 'Save your changes first to share the updated card' : ''}
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#C8A951] text-white hover:bg-[#B09240] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="save-the-date-share-btn"
              aria-label="Share our Save the Date"
            >
              {shareState === 'shared'
                ? <><Check className="w-4 h-4" /> Thank you for sharing</>
                : <><Send className="w-4 h-4" /> Share Our Save the Date</>}
            </button>
            {dirty && (
              <p className="text-[11px] text-amber-700 -mt-1">Unsaved changes — save first so the shared card reflects them.</p>
            )}
            {!canNativeShare && (
              <p className="text-[11px] text-[#8A7A7A] leading-relaxed -mt-1">
                Native share sheet isn't available in this browser — the button will copy the invitation
                to your clipboard so you can paste it into any chat.
              </p>
            )}

            <button
              onClick={copyMessage}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#3B2F2F] text-[#3B2F2F] hover:bg-[#F8F5EF] transition-colors"
              data-testid="save-the-date-copy-message"
            >
              {copyState === 'copied' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copyState === 'copied' ? 'Message copied' : 'Copy invitation text'}
            </button>

            <button
              onClick={copyLink}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#E5E0D8] text-[#3B2F2F] hover:bg-[#F8F5EF] transition-colors"
              data-testid="save-the-date-copy-link"
            >
              <Share2 className="w-4 h-4" /> Copy site link only
            </button>
          </div>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Save the Date to defaults?</DialogTitle>
            <DialogDescription>
              This restores the title, wedding date, message, CTA label, website link and preview image
              to the original values. Your existing uploaded images stay on the server but will no longer
              be linked from the share card until you re-add them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="destructive" onClick={resetDefaults} disabled={resetting} data-testid="save-the-date-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSaveTheDate;
